import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';

const SLIDE_WIDTHS = {
  video:
    'w-[78%] xs:w-[58%] sm:w-[46%] md:w-[36%] lg:w-[30%] xl:w-[24%]',
  continue: 'w-[82%] sm:w-[52%] md:w-[40%] lg:w-[32%]',
  crowdfund: 'w-[70%] sm:w-[44%] md:w-[32%] lg:w-[28%]',
  poster:
    'w-[42%] sm:w-[28%] md:w-[22%] lg:w-[18%] xl:w-[15%]',
  top10:
    'w-[38%] sm:w-[24%] md:w-[18%] lg:w-[14%]',
};

/**
 * EndlessRail — horizontal rail with optional seamless wrap-around.
 */
const EndlessRail = ({
  items,
  renderItem,
  max = 10,
  loop = false,
  className,
  ariaLabel = 'Horizontal rail',
  slideWidth = 'video',
}) => {
  const list = (items ?? []).slice(0, max);
  const slideWidthClass = SLIDE_WIDTHS[slideWidth] ?? SLIDE_WIDTHS.video;

  const trackRef = useRef(null);
  const slideRef = useRef(null);
  const dragRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    moved: false,
  });
  const blockClickRef = useRef(false);

  const [offset, setOffset] = useState(0);
  const [stepPx, setStepPx] = useState(0);
  const [clientW, setClientW] = useState(0);
  const [animating, setAnimating] = useState(true);
  const [dragDx, setDragDx] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (!slideRef.current || !trackRef.current) return;
      const measuredWidth = slideRef.current.getBoundingClientRect().width;
      const styles = window.getComputedStyle(trackRef.current);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      setStepPx(measuredWidth + gap);
      setClientW(trackRef.current.clientWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (trackRef.current) ro.observe(trackRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [list.length, slideWidth]);

  const visibleCount = stepPx ? Math.max(1, Math.round(clientW / stepPx)) : 1;
  const maxOffset = loop ? null : Math.max(0, list.length - visibleCount);

  useEffect(() => {
    if (!loop || list.length === 0) return;
    if (offset >= list.length) {
      const t = setTimeout(() => {
        setAnimating(false);
        setOffset((o) => o - list.length);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setAnimating(true));
        });
      }, 550);
      return () => clearTimeout(t);
    }
    if (offset < 0) {
      const t = setTimeout(() => {
        setAnimating(false);
        setOffset((o) => o + list.length);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setAnimating(true));
        });
      }, 550);
      return () => clearTimeout(t);
    }
  }, [offset, list.length, loop]);

  useEffect(() => {
    if (loop) return;
    if (offset > maxOffset) setOffset(maxOffset);
    if (offset < 0) setOffset(0);
  }, [loop, offset, maxOffset]);

  const canPrev = loop || offset > 0;
  const canNext = loop || offset < maxOffset;

  const next = useCallback(() => {
    if (!canNext) return;
    setOffset((o) => o + 1);
  }, [canNext]);

  const prev = useCallback(() => {
    if (!canPrev) return;
    setOffset((o) => o - 1);
  }, [canPrev]);

  const onPointerDown = (e) => {
    if (!stepPx) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
      pointerId: e.pointerId,
      target: e.currentTarget,
    };
  };

  const onPointerMove = (e) => {
    const s = dragRef.current;
    if (!s.active) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (!s.moved && Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) {
      s.moved = true;
      setAnimating(false);
      try {
        s.target?.setPointerCapture?.(s.pointerId);
      } catch {
        /* ignore */
      }
    }
    if (s.moved) {
      setDragDx(dx);
    }
  };

  const onPointerEnd = (e) => {
    const s = dragRef.current;
    if (!s.active) return;
    s.active = false;
    const dx = dragDx;
    setDragDx(0);

    if (s.moved) {
      setAnimating(true);
      blockClickRef.current = true;
      const slides = Math.round(-dx / stepPx);
      if (slides !== 0) {
        setOffset((o) => {
          const nextOffset = o + slides;
          if (loop) return nextOffset;
          return Math.max(0, Math.min(maxOffset, nextOffset));
        });
      }
      setTimeout(() => {
        blockClickRef.current = false;
      }, 60);
      try {
        e.currentTarget.releasePointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
    }
  };

  const onClickCapture = (e) => {
    if (blockClickRef.current) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  if (list.length === 0) return null;

  const duplicated = loop ? [...list, ...list] : list;
  const translateX = -(offset * stepPx) + dragDx;

  const arrowBase =
    'hidden md:flex absolute top-[40%] h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white/90 backdrop-blur-sm transition-all hover:bg-black/70 z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background';

  return (
    <div
      className={cn('group/rail relative', className)}
      aria-label={ariaLabel}
      role="region"
    >
      <div
        ref={trackRef}
        className="overflow-hidden select-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onClickCapture={onClickCapture}
        style={{
          touchAction: 'pan-y',
          cursor: dragDx !== 0 ? 'grabbing' : 'grab',
        }}
      >
        <div
          className={cn(
            'flex gap-3 md:gap-4',
            animating && 'transition-transform duration-500 ease-out'
          )}
          style={{ transform: `translate3d(${translateX}px, 0, 0)` }}
        >
          {duplicated.map((item, i) => (
            <div
              key={`${item.id ?? i}-${i}`}
              ref={i === 0 ? slideRef : undefined}
              className={cn('flex-none', slideWidthClass)}
            >
              {renderItem(item, i % list.length)}
            </div>
          ))}
        </div>
      </div>

      <div
        className="pointer-events-none absolute right-0 top-0 z-[5] h-full w-8 bg-gradient-to-l from-background to-transparent md:hidden"
        aria-hidden
      />

      <button
        type="button"
        aria-label="Previous"
        onClick={prev}
        disabled={!canPrev}
        className={cn(
          arrowBase,
          'left-0 -translate-x-4',
          canPrev
            ? 'opacity-0 group-hover/rail:opacity-100 focus:opacity-100'
            : 'pointer-events-none opacity-0'
        )}
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        aria-label="Next"
        onClick={next}
        disabled={!canNext}
        className={cn(
          arrowBase,
          'right-0 translate-x-4',
          canNext
            ? 'opacity-0 group-hover/rail:opacity-100 focus:opacity-100'
            : 'pointer-events-none opacity-0'
        )}
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
};

export default EndlessRail;
